import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search, SlidersHorizontal } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CaseCard, EmptyState } from "@/components/CaseCard";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Cases() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: meta } = useMeta();
  const [campus, setCampus] = useState("all");
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  const params = {};
  if (campus !== "all") params.campus = campus;
  if (status !== "all") params.status = status;

  const { data, isLoading } = useQuery({
    queryKey: ["cases", campus, status],
    queryFn: async () => (await api.get("/cases", { params })).data,
  });

  const filtered = (data || []).filter((c) =>
    !q || c.employee_name.toLowerCase().includes(q.toLowerCase()) || (c.manager_name || "").toLowerCase().includes(q.toLowerCase())
  );

  const canFilterCampus = user.role !== "manager";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-patria-ink">Cases</h1>
          <p className="text-patria-muted mt-1">{user.role === "manager" ? "Cases for your team." : "All COMPASS cases across campuses."}</p>
        </div>
        {(user.role === "manager" || user.role === "leadership") && (
          <Button onClick={() => navigate("/cases/new")} data-testid="cases-new-button" className="rounded-full bg-patria-terracotta hover:bg-patria-terracottaDeep text-patria-surface">
            <Plus className="h-4 w-4 mr-2" /> New case
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-patria-surface border border-patria-muted/15 p-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-patria-muted" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee or manager…" data-testid="cases-search" className="pl-9 bg-patria-surface border-patria-muted/30 focus-visible:ring-patria-green" />
        </div>
        {canFilterCampus && (
          <Select value={campus} onValueChange={setCampus}>
            <SelectTrigger className="w-[180px] bg-patria-surface border-patria-muted/30" data-testid="cases-filter-campus"><SelectValue placeholder="Campus" /></SelectTrigger>
            <SelectContent className="bg-patria-surface">
              <SelectItem value="all">All campuses</SelectItem>
              {meta?.campuses?.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-[170px] bg-patria-surface border-patria-muted/30" data-testid="cases-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-patria-surface">
            <SelectItem value="all">All statuses</SelectItem>
            {meta?.pipeline?.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}</div>
      ) : filtered.length === 0 ? (
        <EmptyState title="No cases found" hint="Adjust filters or start a new case." testId="empty-cases" />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="cases-grid">
          {filtered.map((c) => <CaseCard key={c.id} c={c} showManager={user.role !== "manager"} />)}
        </div>
      )}
    </div>
  );
}
